import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated blog posts list
 * with search and tag filtering capabilities.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [stickyBar] - The sticky search/filter bar
 * @property {HTMLFormElement} [searchForm] - The search form
 * @property {HTMLInputElement} [searchInput] - The search input
 * @property {HTMLElement} [filters] - The filter pills container
 * @property {HTMLElement} [filterPills] - The scrollable pills container
 * @property {HTMLButtonElement} [filterPrev] - Previous arrow button
 * @property {HTMLButtonElement} [filterNext] - Next arrow button
 * @property {HTMLElement} [filterFadeLeft] - Left fade overlay
 * @property {HTMLElement} [filterFadeRight] - Right fade overlay
 * @property {HTMLElement} [featuredArticle] - The featured article block
 * @property {HTMLElement} [sectionHeading] - The "Featured Blogs" heading
 * @property {HTMLElement} [noResults] - The no results message
 * @property {HTMLElement} [grid] - The blog posts grid
 */
export default class BlogPostsList extends PaginatedList {
  /** @type {IntersectionObserver | undefined} */
  #stickyObserver;

  /** @type {MutationObserver | undefined} */
  #gridObserver;

  /** @type {string} */
  #searchQuery = '';

  connectedCallback() {
    super.connectedCallback();
    this.#initSearch();
    this.#setupStickyBar();
    this.#setupSearchForm();
    this.#setupFilterScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stickyObserver?.disconnect();
    this.#gridObserver?.disconnect();
  }

  /**
   * Initialize search state from URL params.
   * If ?q= is present, enter search mode and filter visible cards.
   */
  #initSearch() {
    const url = new URL(window.location.href);
    const query = url.searchParams.get('q');

    if (!query) return;

    this.#searchQuery = query.trim().toLowerCase();

    if (!this.#searchQuery) return;

    // Enter search mode: hide filters, featured article, section heading
    this.setAttribute('data-searching', 'true');

    // Populate search input with current query
    const { searchInput } = this.refs;
    if (searchInput) {
      searchInput.value = query.trim();
    }

    // Filter currently visible cards
    this.#filterCards();

    // Watch for new cards appended by infinite scroll
    this.#observeGrid();
  }

  /**
   * Filter blog post cards based on the search query.
   * Hides cards whose title and content don't match the query.
   */
  #filterCards() {
    const { cards, noResults, grid } = this.refs;

    if (!Array.isArray(cards) || !this.#searchQuery) return;

    let visibleCount = 0;

    for (const card of cards) {
      const title = (card.dataset.title || '').toLowerCase();
      const content = (card.dataset.content || '').toLowerCase();
      const matches = title.includes(this.#searchQuery) || content.includes(this.#searchQuery);

      card.style.display = matches ? '' : 'none';
      if (matches) visibleCount++;
    }

    // Show/hide no results message
    if (noResults) {
      noResults.hidden = visibleCount > 0;
    }
  }

  /**
   * Watch for new cards appended to the grid (from infinite scroll)
   * and apply search filtering to them.
   */
  #observeGrid() {
    const { grid } = this.refs;
    if (!grid) return;

    this.#gridObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (!node.hasAttribute('ref') || !node.getAttribute('ref')?.includes('cards')) continue;

          const title = (node.dataset.title || '').toLowerCase();
          const content = (node.dataset.content || '').toLowerCase();
          const matches = title.includes(this.#searchQuery) || content.includes(this.#searchQuery);
          node.style.display = matches ? '' : 'none';
        }
      }
    });

    this.#gridObserver.observe(grid, { childList: true });
  }

  /**
   * Sets up the IntersectionObserver for the sticky bar shadow effect.
   * Uses a sentinel element: when it scrolls out of view, the bar is "stuck".
   */
  #setupStickyBar() {
    const { stickyBar } = this.refs;
    if (!stickyBar) return;

    const sentinel = document.createElement('div');
    sentinel.className = 'blog-sticky-sentinel';
    sentinel.style.height = '1px';
    sentinel.style.width = '100%';
    sentinel.style.pointerEvents = 'none';
    stickyBar.before(sentinel);

    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        stickyBar.classList.toggle('blog-sticky-bar--stuck', !entry.isIntersecting);
      },
      { threshold: 0 }
    );

    this.#stickyObserver.observe(sentinel);
  }

  /**
   * Sets up horizontal scroll controls for the filter pills.
   * Shows/hides arrow buttons and fade overlays based on scroll position.
   */
  #setupFilterScroll() {
    const { filterPills, filterPrev, filterNext, filterFadeLeft, filterFadeRight } = this.refs;
    if (!filterPills) return;

    const scrollAmount = 200;

    const updateControls = () => {
      const { scrollLeft, scrollWidth, clientWidth } = filterPills;
      const atStart = scrollLeft <= 0;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      filterPrev?.toggleAttribute('hidden', atStart);
      filterNext?.toggleAttribute('hidden', atEnd);
      filterFadeLeft?.toggleAttribute('hidden', atStart);
      filterFadeRight?.toggleAttribute('hidden', atEnd);
    };

    filterPills.addEventListener('scroll', updateControls, { passive: true });
    filterPrev?.addEventListener('click', () => {
      filterPills.scrollBy({ left: -scrollAmount });
    });
    filterNext?.addEventListener('click', () => {
      filterPills.scrollBy({ left: scrollAmount });
    });

    // Initial check after layout settles
    requestAnimationFrame(updateControls);
  }

  /**
   * Sets up the search form to navigate with the q parameter.
   * Strips any tag filter from the URL when searching.
   */
  #setupSearchForm() {
    const { searchForm, searchInput } = this.refs;
    if (!searchForm || !searchInput) return;

    searchForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const query = searchInput.value.trim();

      // Build URL: base blog path (strip /tagged/...) with ?q= param
      const currentUrl = new URL(window.location.href);
      const basePath = currentUrl.pathname.split('/tagged/')[0];
      const newUrl = new URL(basePath, currentUrl.origin);

      if (query) {
        newUrl.searchParams.set('q', query);
      }
      // Remove pagination param
      newUrl.searchParams.delete('page');

      window.location.href = newUrl.toString();
    });
  }

}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
